import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Table,
  UncontrolledTooltip,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { intlLocaleFromI18n } from "../../../helpers/money";
import {
  downloadSaReputationExport,
  exportSaReputation,
  formatSaRate,
  getSaReputation,
  getSaReputationExport,
  SaReputationCircuitStatus,
  SaReputationRow,
  SaReputationThresholds,
  SaReputationWindowStats,
} from "../../../helpers/super_admin_ops_helper";

const PER_PAGE = 25;
const SEARCH_DEBOUNCE_MS = 350;
/** Delta mínimo de bounce % (24h vs 7d) para marcar tendência. */
const TREND_DELTA = 0.5;
/** Poll do export assíncrono (fila default). */
const EXPORT_POLL_MS = 1500;
const EXPORT_POLL_MAX_ATTEMPTS = 60;

type WindowKey = "24h" | "7d";
type RiskPreset = "all" | "at_risk" | "circuit_open" | "needs_sa" | "byo_ceiling";
type BounceTrend = "up" | "down" | "flat";

const circuitBadgeColor = (status: SaReputationCircuitStatus): string => {
  if (status === "open") return "danger";
  if (status === "warning") return "warning";
  return "success";
};

/** Compara bounce 24h com 7d — piora recente = seta vermelha (ops rápido). */
const bounceTrend = (row: SaReputationRow): BounceTrend => {
  const d = row.window_24h.bounce_rate - row.window_7d.bounce_rate;
  if (d >= TREND_DELTA) return "up";
  if (d <= -TREND_DELTA) return "down";
  return "flat";
};

/** Bounce total + hard/soft clicável → envios do cliente (ops). */
const BounceCell = ({
  primary,
  secondary,
  locale,
  tenantId,
  hardSoftLinkTitle,
}: {
  primary: SaReputationWindowStats;
  secondary: SaReputationWindowStats;
  locale: string;
  tenantId: number;
  hardSoftLinkTitle: string;
}) => (
  <td className="text-end">
    <div>{formatSaRate(primary.bounce_rate, locale)}</div>
    <div className="text-muted small">
      {formatSaRate(secondary.bounce_rate, locale)}
    </div>
    <Link
      to={`/super-admin/sending?tenant_id=${tenantId}`}
      className="small mt-1 d-inline-block text-decoration-none"
      style={{ lineHeight: 1.3 }}
      title={hardSoftLinkTitle}
    >
      <span className="text-danger">
        H {formatSaRate(primary.hard_bounce_rate ?? 0, locale)}
      </span>
      {" · "}
      <span className="text-warning">
        S {formatSaRate(primary.soft_bounce_rate ?? 0, locale)}
      </span>
    </Link>
  </td>
);

const DualRateCell = ({
  primary,
  secondary,
  locale,
}: {
  primary: number;
  secondary: number;
  locale: string;
}) => (
  <td className="text-end">
    <div>{formatSaRate(primary, locale)}</div>
    <div className="text-muted small">{formatSaRate(secondary, locale)}</div>
  </td>
);

const DualSentCell = ({
  primary,
  secondary,
  locale,
}: {
  primary: number;
  secondary: number;
  locale: string;
}) => (
  <td className="text-end">
    <div>{primary.toLocaleString(locale)}</div>
    <div className="text-muted small">{secondary.toLocaleString(locale)}</div>
  </td>
);

/** Mini barras hard (danger) / soft (warning) dos últimos 7 dias — clique = Envios do dia. */
const BounceSparklineCell = ({
  points,
  tenantId,
}: {
  points?: Array<{
    date: string;
    hard_bounced: number;
    soft_bounced: number;
  }>;
  tenantId: number;
}) => {
  const series = points ?? [];
  if (series.length === 0) {
    return <td className="text-muted small">—</td>;
  }
  const max = Math.max(
    1,
    ...series.map((p) => Math.max(p.hard_bounced, p.soft_bounced))
  );
  return (
    <td style={{ minWidth: 72 }}>
      <div
        className="d-flex align-items-end gap-1"
        style={{ height: 28 }}
      >
        {series.map((p) => (
          <Link
            key={p.date}
            to={`/super-admin/sending?tenant_id=${tenantId}&date=${encodeURIComponent(p.date)}`}
            className="flex-fill d-flex flex-column justify-content-end align-items-center text-decoration-none"
            title={`${p.date}: H ${p.hard_bounced} · S ${p.soft_bounced}`}
            style={{ minWidth: 0 }}
          >
            <div
              className="w-100 rounded-top bg-danger"
              style={{
                height: `${Math.round((p.hard_bounced / max) * 100)}%`,
                minHeight: p.hard_bounced > 0 ? 3 : 1,
                opacity: 0.85,
              }}
            />
            <div
              className="w-75 rounded-top bg-warning"
              style={{
                height: `${Math.round((p.soft_bounced / max) * 100)}%`,
                minHeight: p.soft_bounced > 0 ? 2 : 0,
                marginTop: 1,
              }}
            />
          </Link>
        ))}
      </div>
    </td>
  );
};

/**
 * Ranking de reputação por cliente (LAC-052 · DOC 02 § 6.2).
 * Por quê: o dashboard só mostra totais — aqui Roberto acha quem está sujando a conta.
 */
const SuperAdminReputation = () => {
  const { t, i18n } = useTranslation();
  const locale = intlLocaleFromI18n(i18n.language);
  const [searchParams, setSearchParams] = useSearchParams();

  const initialWindow =
    searchParams.get("window") === "7d" ? "7d" : ("24h" as WindowKey);
  const initialPreset: RiskPreset =
    searchParams.get("needs_sa_resume") === "1"
      ? "needs_sa"
      : searchParams.get("byo_near_ceiling") === "1"
        ? "byo_ceiling"
        : searchParams.get("circuit_open") === "1"
          ? "circuit_open"
          : searchParams.get("at_risk") === "1"
            ? "at_risk"
            : "all";

  const [items, setItems] = useState<SaReputationRow[]>([]);
  const [thresholds, setThresholds] = useState<SaReputationThresholds | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [windowKey, setWindowKey] = useState<WindowKey>(initialWindow);
  const [preset, setPreset] = useState<RiskPreset>(initialPreset);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [appliedSearch, setAppliedSearch] = useState(
    searchParams.get("search") || ""
  );
  const [sort, setSort] = useState<
    "risk" | "bounce_rate" | "complaint_rate" | "sent" | "name"
  >("risk");

  document.title = t("sa.reputation.documentTitle");

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setAppliedSearch(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (windowKey === "7d") next.set("window", "7d");
    if (preset === "at_risk") next.set("at_risk", "1");
    if (preset === "circuit_open") next.set("circuit_open", "1");
    if (preset === "needs_sa") next.set("needs_sa_resume", "1");
    if (preset === "byo_ceiling") next.set("byo_near_ceiling", "1");
    if (appliedSearch) next.set("search", appliedSearch);
    setSearchParams(next, { replace: true });
  }, [windowKey, preset, appliedSearch, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSaReputation({
        window: windowKey,
        at_risk: preset === "at_risk" ? 1 : undefined,
        circuit_open: preset === "circuit_open" ? 1 : undefined,
        needs_sa_resume: preset === "needs_sa" ? 1 : undefined,
        byo_near_ceiling: preset === "byo_ceiling" ? 1 : undefined,
        search: appliedSearch || undefined,
        sort,
        page,
        per_page: PER_PAGE,
      });
      if (!res?.success) {
        setError(t("sa.reputation.error.load"));
        setItems([]);
        return;
      }
      setItems(res.data || []);
      setThresholds(res.thresholds || null);
      setTotal(res.meta?.total ?? 0);
      setLastPage(res.meta?.last_page ?? 1);
    } catch (e: any) {
      setError(e?.message || t("sa.reputation.error.load"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [windowKey, preset, appliedSearch, sort, page, t]);

  useEffect(() => {
    load();
  }, [load]);

  const primaryOf = (row: SaReputationRow): SaReputationWindowStats =>
    windowKey === "7d" ? row.window_7d : row.window_24h;
  const secondaryOf = (row: SaReputationRow): SaReputationWindowStats =>
    windowKey === "7d" ? row.window_24h : row.window_7d;

  const clearFilters = () => {
    setPreset("all");
    setSearch("");
    setAppliedSearch("");
    setWindowKey("24h");
    setSort("risk");
    setPage(1);
  };

  const exportCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      // Preferir API (inclui colunas hard/soft); sync ≤100, async + poll acima.
      const filters = {
        window: windowKey,
        at_risk: preset === "at_risk" ? (1 as const) : undefined,
        circuit_open: preset === "circuit_open" ? (1 as const) : undefined,
        needs_sa_resume: preset === "needs_sa" ? (1 as const) : undefined,
        byo_near_ceiling: preset === "byo_ceiling" ? (1 as const) : undefined,
        search: appliedSearch || undefined,
        sort,
      };
      const started = await exportSaReputation(filters);
      if (!started?.success || !started.data?.id) {
        setError(t("sa.reputation.error.export"));
        return;
      }

      let session = started.data;
      if (!session.download_ready && session.status !== "completed") {
        for (let i = 0; i < EXPORT_POLL_MAX_ATTEMPTS; i += 1) {
          await new Promise((r) => window.setTimeout(r, EXPORT_POLL_MS));
          const poll = await getSaReputationExport(session.id);
          if (!poll?.success || !poll.data) {
            throw new Error(t("sa.reputation.error.export"));
          }
          session = poll.data;
          if (session.status === "completed" || session.download_ready) {
            break;
          }
          if (session.status === "failed") {
            throw new Error(
              session.error_message || t("sa.reputation.error.export")
            );
          }
        }
      }

      if (!session.download_ready && session.status !== "completed") {
        setError(t("sa.reputation.error.exportTimeout"));
        return;
      }
      if ((session.row_count ?? 0) === 0) {
        setError(t("sa.reputation.error.exportEmpty"));
        return;
      }

      const stamp = new Date().toISOString().slice(0, 10);
      await downloadSaReputationExport(
        session.id,
        `reputation-${stamp}.csv`
      );
    } catch (e: any) {
      setError(e?.message || t("sa.reputation.error.export"));
    } finally {
      setExporting(false);
    }
  };

  const trendIcon = (trend: BounceTrend): string => {
    if (trend === "up") return "ri-arrow-up-line text-danger";
    if (trend === "down") return "ri-arrow-down-line text-success";
    return "ri-subtract-line text-muted";
  };

  const primaryLabel =
    windowKey === "7d"
      ? t("sa.reputation.window.7d")
      : t("sa.reputation.window.24h");
  const secondaryLabel =
    windowKey === "7d"
      ? t("sa.reputation.window.24h")
      : t("sa.reputation.window.7d");

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.reputation.title")}
          pageTitle={t("sa.menu.reputation")}
        />

        <Row className="mb-3">
          <Col lg={12}>
            <Alert color="info" className="mb-0 py-2">
              {t("sa.reputation.intro")}
              {thresholds ? (
                <>
                  {" "}
                  {t("sa.reputation.thresholdsHint", {
                    bounce: formatSaRate(thresholds.bounce_rate, locale),
                    complaint: formatSaRate(thresholds.complaint_rate, locale),
                    min: thresholds.min_sent.toLocaleString(locale),
                  })}
                </>
              ) : null}{" "}
              {t("sa.reputation.dualHint", {
                primary: primaryLabel,
                secondary: secondaryLabel,
              })}
            </Alert>
          </Col>
        </Row>

        <Card>
          <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
            <h5 className="card-title mb-0">{t("sa.reputation.tableTitle")}</h5>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <ButtonGroup size="sm">
                <Button
                  color={windowKey === "24h" ? "primary" : "light"}
                  onClick={() => {
                    setWindowKey("24h");
                    setPage(1);
                  }}
                >
                  {t("sa.reputation.window.24h")}
                </Button>
                <Button
                  color={windowKey === "7d" ? "primary" : "light"}
                  onClick={() => {
                    setWindowKey("7d");
                    setPage(1);
                  }}
                >
                  {t("sa.reputation.window.7d")}
                </Button>
              </ButtonGroup>
              <ButtonGroup size="sm">
                <Button
                  color={preset === "all" ? "primary" : "light"}
                  onClick={() => {
                    setPreset("all");
                    setPage(1);
                  }}
                >
                  {t("sa.reputation.filter.all")}
                </Button>
                <Button
                  color={preset === "at_risk" ? "warning" : "light"}
                  onClick={() => {
                    setPreset("at_risk");
                    setPage(1);
                  }}
                >
                  {t("sa.reputation.filter.atRisk")}
                </Button>
                <Button
                  color={preset === "circuit_open" ? "danger" : "light"}
                  onClick={() => {
                    setPreset("circuit_open");
                    setPage(1);
                  }}
                >
                  {t("sa.reputation.filter.circuitOpen")}
                </Button>
                <Button
                  color={preset === "needs_sa" ? "danger" : "light"}
                  onClick={() => {
                    setPreset("needs_sa");
                    setPage(1);
                  }}
                >
                  {t("sa.reputation.filter.needsSa")}
                </Button>
                <Button
                  color={preset === "byo_ceiling" ? "danger" : "light"}
                  onClick={() => {
                    setPreset("byo_ceiling");
                    setPage(1);
                  }}
                >
                  {t("sa.reputation.filter.byoCeiling")}
                </Button>
              </ButtonGroup>
              <Input
                type="search"
                bsSize="sm"
                style={{ maxWidth: 220 }}
                placeholder={t("sa.reputation.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label={t("sa.reputation.searchPlaceholder")}
              />
              <Input
                type="select"
                bsSize="sm"
                style={{ maxWidth: 180 }}
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value as typeof sort);
                  setPage(1);
                }}
                aria-label={t("sa.reputation.sortLabel")}
              >
                <option value="risk">{t("sa.reputation.sort.risk")}</option>
                <option value="bounce_rate">
                  {t("sa.reputation.sort.bounce")}
                </option>
                <option value="complaint_rate">
                  {t("sa.reputation.sort.complaint")}
                </option>
                <option value="sent">{t("sa.reputation.sort.sent")}</option>
                <option value="name">{t("sa.reputation.sort.name")}</option>
              </Input>
              <Button
                color="soft-secondary"
                size="sm"
                disabled={exporting || loading}
                onClick={() => exportCsv()}
              >
                {exporting ? (
                  <Spinner size="sm" />
                ) : (
                  t("sa.reputation.exportCsv")
                )}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {error ? (
              <Alert color="danger" className="mb-3">
                {error}{" "}
                <button
                  type="button"
                  className="btn btn-sm btn-soft-danger ms-2"
                  onClick={() => load()}
                >
                  {t("sa.reputation.retry")}
                </button>
              </Alert>
            ) : null}

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : items.length === 0 ? (
              <Alert color="secondary" className="mb-0">
                <p className="mb-2">{t("sa.reputation.empty")}</p>
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    tag={Link}
                    to="/super-admin/sending"
                    color="primary"
                    size="sm"
                  >
                    {t("sa.reputation.emptyCtaSending")}
                  </Button>
                  {(preset !== "all" || appliedSearch) && (
                    <Button color="light" size="sm" onClick={clearFilters}>
                      {t("sa.reputation.emptyCtaClear")}
                    </Button>
                  )}
                </div>
              </Alert>
            ) : (
              <>
                <div className="table-responsive">
                  <Table className="table-nowrap align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t("sa.reputation.col.client")}</th>
                        <th>{t("sa.reputation.col.status")}</th>
                        <th className="text-center">
                          {t("sa.reputation.col.trend")}
                        </th>
                        <th className="text-center">
                          {t("sa.reputation.col.sparkline")}
                        </th>
                        <th className="text-end">
                          {t("sa.reputation.col.sent")}
                          <div className="fw-normal text-muted small">
                            {primaryLabel} / {secondaryLabel}
                          </div>
                        </th>
                        <th className="text-end">
                          {t("sa.reputation.col.bounce")}
                          <div className="fw-normal text-muted small">
                            {primaryLabel} / {secondaryLabel}
                          </div>
                          <div className="fw-normal text-muted small">
                            {t("sa.reputation.col.hardSoftHint")}
                          </div>
                        </th>
                        <th className="text-end">
                          {t("sa.reputation.col.complaint")}
                          <div className="fw-normal text-muted small">
                            {primaryLabel} / {secondaryLabel}
                          </div>
                        </th>
                        <th className="text-end">
                          {t("sa.reputation.col.unsub")}
                          <div className="fw-normal text-muted small">
                            {primaryLabel} / {secondaryLabel}
                          </div>
                        </th>
                        <th className="text-end">
                          {t("sa.reputation.col.apiFailed")}
                          <div className="fw-normal text-muted small">
                            {t("sa.reputation.col.apiFailedHint")}
                          </div>
                        </th>
                        <th className="text-end">
                          {t("sa.reputation.col.awaitingSa")}
                          <div className="fw-normal text-muted small">
                            {t("sa.reputation.col.awaitingSaHint")}
                          </div>
                        </th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((row) => {
                        const primary = primaryOf(row);
                        const secondary = secondaryOf(row);
                        const trend = bounceTrend(row);
                        const tipId = `rep-trend-${row.tenant_id}`;
                        return (
                          <tr key={row.tenant_id}>
                            <td>
                              <Link
                                to={`/super-admin/tenants/${row.tenant_id}`}
                                className="fw-medium"
                              >
                                {row.tenant_name}
                              </Link>
                              {row.blocked ? (
                                <Badge color="dark" className="ms-2">
                                  {t("sa.reputation.badge.blocked")}
                                </Badge>
                              ) : null}
                            </td>
                            <td>
                              <Badge
                                color={circuitBadgeColor(row.circuit_status)}
                                className="text-uppercase"
                              >
                                {t(
                                  `sa.reputation.status.${row.circuit_status}`
                                )}
                              </Badge>
                              {row.sending_account_mode === "customer_aws" ? (
                                <Badge
                                  color={
                                    row.byo_circuit?.near_aws_ceiling
                                      ? "danger"
                                      : "info"
                                  }
                                  className="ms-1"
                                  title={
                                    row.byo_circuit
                                      ? t("sa.reputation.byoThresholds", {
                                          bounce: row.byo_circuit.bounce_pct,
                                          complaint:
                                            row.byo_circuit.complaint_pct,
                                        })
                                      : undefined
                                  }
                                >
                                  {row.byo_circuit?.near_aws_ceiling
                                    ? t("sa.reputation.badge.byoCeiling")
                                    : t("sa.reputation.badge.byo")}
                                </Badge>
                              ) : null}
                              {(row.sends_awaiting_sa_resume ?? 0) > 0 ? (
                                <Badge color="danger" className="ms-1">
                                  {t("sa.reputation.badge.needsSa", {
                                    count: row.sends_awaiting_sa_resume,
                                  })}
                                </Badge>
                              ) : null}
                            </td>
                            <td className="text-center">
                              <i
                                id={tipId}
                                className={`${trendIcon(trend)} fs-16`}
                                aria-label={t(
                                  `sa.reputation.trend.${trend}`
                                )}
                              />
                              <UncontrolledTooltip target={tipId} placement="top">
                                {t(`sa.reputation.trend.${trend}`)}
                              </UncontrolledTooltip>
                            </td>
                            <BounceSparklineCell
                              points={row.sparkline_7d}
                              tenantId={row.tenant_id}
                            />
                            <DualSentCell
                              primary={primary.sent}
                              secondary={secondary.sent}
                              locale={locale}
                            />
                            <BounceCell
                              primary={primary}
                              secondary={secondary}
                              locale={locale}
                              tenantId={row.tenant_id}
                              hardSoftLinkTitle={t(
                                "sa.reputation.hardSoftLinkHint"
                              )}
                            />
                            <DualRateCell
                              primary={primary.complaint_rate}
                              secondary={secondary.complaint_rate}
                              locale={locale}
                            />
                            <DualRateCell
                              primary={primary.unsubscribe_rate}
                              secondary={secondary.unsubscribe_rate}
                              locale={locale}
                            />
                            <td className="text-end">
                              <div className="fw-medium">
                                {(row.api_failed_today ?? 0).toLocaleString(
                                  locale
                                )}
                              </div>
                              <div className="text-muted small">
                                7d:{" "}
                                {(row.api_failed_7d ?? 0).toLocaleString(
                                  locale
                                )}
                              </div>
                            </td>
                            <td className="text-end">
                              {(row.sends_awaiting_sa_resume ?? 0) > 0 ? (
                                <Link
                                  to={`/super-admin/sending?needs_sa_resume=1&tenant_id=${row.tenant_id}`}
                                  className="fw-semibold text-danger"
                                >
                                  {(
                                    row.sends_awaiting_sa_resume ?? 0
                                  ).toLocaleString(locale)}
                                </Link>
                              ) : (
                                <span className="text-muted">0</span>
                              )}
                            </td>
                            <td className="text-end">
                              <div className="d-inline-flex flex-wrap gap-1 justify-content-end">
                                <Button
                                  tag={Link}
                                  to={`/super-admin/tenants/${row.tenant_id}`}
                                  color="soft-primary"
                                  size="sm"
                                >
                                  {t("sa.reputation.openDetail")}
                                </Button>
                                <Button
                                  tag={Link}
                                  to={`/super-admin/sending?tenant_id=${row.tenant_id}`}
                                  color="soft-secondary"
                                  size="sm"
                                >
                                  {t("sa.reputation.openSending")}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="text-muted small">
                    {t("sa.reputation.pagination", {
                      total: total.toLocaleString(locale),
                      page,
                      last: lastPage,
                    })}
                  </span>
                  <div className="d-flex gap-2">
                    <Button
                      color="light"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      {t("sa.reputation.prev")}
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      disabled={page >= lastPage}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      {t("sa.reputation.next")}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default SuperAdminReputation;
